import { useState } from 'react'

import SaveIcon from '@mui/icons-material/Save'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { getISOWeek, parseISO } from 'date-fns'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import { StartUitvoeringWarning } from '@components/StartUitvoeringWarning'
import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'
import type { FilterOption } from '@models/Filter'
import {
  beginningOfYearDateForISOWeek,
  formatWeekYearToISOWeek,
} from '@util/date-util'
import { makeIsGeschovenNaarAannemerAllowed } from '@util/project-util'

import { SelectObject } from '../../../../core/components/SelectObject'
import { SelectString } from '../../../../core/components/SelectString'
import type { HoofdleidingProject } from '../../../../hoofdleiding/project/types/HoofdleidingProject'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import type { BijlageTemplate } from '../../../../templates/types/BijlageTemplate'
import { StyledAlert } from '../../../../theme/components/StyledAlert'
import type { NetbeheerderDiscipline } from '../../../../valuelists/types/NetbeheerderDiscipline'

import { GeschovenNaarAannemer } from './GeschovenNaarAannemer'
import { hasProjectErrors, validateProject } from './validateProject'
import type { ProjectErrors } from './validateProject'

export { CreateEditProjectForm }
export type { CreateEditProjectFormOptions }

const initialProject: Partial<HoofdleidingProject> = {
  aannemers: [],
  adres: '',
  coordAannemer: [],
  disciplineVerzamelingen: [],
  doorlooptijdWeken: undefined,
  gemeente: undefined,
  externeReferentie: '',
  naam: '',
  netbeheerders: [],
  startUitvoeringWeek: undefined,
  type: 'Hoofdleiding',
  typeWerk: undefined,
  statusText: 'Intake',
  labels: [],
  tracelengte: 0,
  woningen: 0,
  appartementen: 0,
  bedrijfsunits: 0,
}

type CreateEditProjectFormOptions = {
  gemeenten: string[]
  typeWerkTypes: string[]
  gebieden: FilterOption[]
  aannemers: string[]
  labels: string[]
  netbeheerderDisciplines: NetbeheerderDiscipline[]
}

type Props = {
  type: 'create' | 'edit'
  projectInfo?: Partial<HoofdleidingProject>
  options: CreateEditProjectFormOptions
  geblokkeerdeWeken: GeblokkeerdeWeek[]
  bijlagetemplates?: BijlageTemplate[]
  canCreateWijzigenGeschovenNaarAannemer?: boolean
  isPending?: boolean
  // The context options depend on the chosen gebied, so the dialog needs to know it
  onGebiedscodeChange: (gebiedscode?: string) => void
  onSubmit: (project: Partial<HoofdleidingProject>) => void
  handleClose: () => void
}

const CreateEditProjectForm = ({
  type,
  projectInfo,
  options: {
    gemeenten,
    typeWerkTypes,
    gebieden: gebiedOptions,
    aannemers,
    labels: selectableLabels,
    netbeheerderDisciplines,
  },
  geblokkeerdeWeken,
  bijlagetemplates = [],
  canCreateWijzigenGeschovenNaarAannemer,
  isPending,
  onGebiedscodeChange,
  onSubmit,
  handleClose,
}: Props) => {
  const [project, setProject] = useState(() => ({
    ...initialProject,
    ...projectInfo,
  }))
  const [startUitvoeringWeek, setStartUitvoeringWeek] = useState(() =>
    projectInfo?.startUitvoeringWeek
      ? getISOWeek(parseISO(projectInfo.startUitvoeringWeek))
      : undefined,
  )
  const [startUitvoeringJaar, setStartUitvoeringJaar] = useState(() =>
    projectInfo?.startUitvoeringWeek
      ? beginningOfYearDateForISOWeek(projectInfo.startUitvoeringWeek)
      : null,
  )
  const [aanvraagDatum, setAanvraagDatum] = useState(() =>
    projectInfo?.aanvraagDatum ? new Date(projectInfo.aanvraagDatum) : null,
  )
  const [errors, setErrors] = useState<ProjectErrors>({})

  const selectedGebied =
    gebiedOptions.find(({ code }) => code === project.gebiedscode) || null

  const isUpdateProject = type === 'edit'

  const isEditTypeWerkAllowed = bijlagetemplates.every(
    (template) => !template.typeWerk,
  )
  const isGeschovenNaarAannemerAllowed = makeIsGeschovenNaarAannemerAllowed(
    project.gebiedscode,
  )
  const hasGeschovenNaarAannemer =
    canCreateWijzigenGeschovenNaarAannemer &&
    project.disciplineVerzamelingen &&
    project.disciplineVerzamelingen.some(isGeschovenNaarAannemerAllowed)

  const parseInt0 = (value: string | number) => {
    const parsed = parseInt(value.toString())
    /* v8 ignore next -- @preserve */
    if (isNaN(parsed)) {
      return 0
    } else {
      return parsed
    }
  }

  const handleSubmit = () => {
    const errors = validateProject({
      project,
      startUitvoeringWeek,
      startUitvoeringJaar,
    })
    setErrors(errors)

    if (hasProjectErrors(errors)) return

    const request: Partial<HoofdleidingProject> = { ...project }

    if (startUitvoeringJaar && startUitvoeringWeek) {
      request.startUitvoeringWeek = formatWeekYearToISOWeek(
        startUitvoeringJaar,
        startUitvoeringWeek,
      )
    }

    if (aanvraagDatum) {
      request.aanvraagDatum = aanvraagDatum.toISOString()
    }

    onSubmit(request)
  }

  const handleDisciplineUpdate = ({
    disciplineCode,
    netbeheerderCode,
    isGeschovenNaarAannemer,
  }: DisciplineVerzameling) => {
    const disc = project.disciplineVerzamelingen?.map((d) => {
      if (
        d.disciplineCode === disciplineCode &&
        d.netbeheerderCode === netbeheerderCode
      ) {
        return {
          ...d,
          isGeschovenNaarAannemer,
        }
      }

      return d
    })

    setProject((project) => ({
      ...project,
      /* v8 ignore next - TODO: missing coverage -- @preserve */
      disciplineVerzamelingen: disc || undefined,
    }))
  }

  return (
    <Dialog open fullWidth maxWidth={'md'}>
      <DialogTitle>
        {isUpdateProject ? 'Wijzigen' : 'Aanmaken'} project
      </DialogTitle>

      <DialogContent>
        <StartUitvoeringWarning
          jaar={startUitvoeringJaar}
          week={startUitvoeringWeek}
        />

        <GeblokkeerdeWekenWarning
          geblokkeerdeWeken={geblokkeerdeWeken}
          isoWeek={formatWeekYearToISOWeek(
            startUitvoeringJaar,
            startUitvoeringWeek,
          )}
          gebiedscode={project.gebiedscode || ''}
        />

        <Box sx={{ m: 'auto', width: '95%', p: 1 }}>
          <FormControl sx={{ width: '100%', p: 1 }}>
            <TextField
              variant="standard"
              value="Hoofdleiding"
              label="Type"
              disabled
            />
          </FormControl>
          <FormControl
            sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'row' }}
          >
            <TextField
              sx={{ width: '50%', pr: 2 }}
              variant="standard"
              value={project.externeReferentie}
              label="Externe referentie"
              onChange={(e) =>
                setProject((project) => ({
                  ...project,
                  externeReferentie: e.target.value,
                }))
              }
              error={errors.externeReferentie}
              slotProps={{ htmlInput: { maxLength: 25 } }}
            />
            <TextField
              sx={{ width: '50%' }}
              variant="standard"
              value={project.naam}
              label="Naam"
              onChange={(e) =>
                setProject((project) => ({ ...project, naam: e.target.value }))
              }
              error={errors.naam}
            />
          </FormControl>
          <FormControl
            sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'row' }}
          >
            <TextField
              disabled
              sx={{ width: '50%', pr: 2 }}
              value={
                project.statusText ||
                /* v8 ignore next - TODO: missing coverage -- @preserve */ ''
              }
              label="Processtap"
              variant="standard"
            />

            <SelectString
              options={gemeenten}
              sx={{ width: '50%' }}
              onChange={(gemeente) =>
                setProject((project) => ({
                  ...project,
                  gemeente:
                    gemeente ||
                    /* v8 ignore next - TODO: missing coverage -- @preserve */ '',
                }))
              }
              value={project.gemeente || ''}
              label="Gemeente"
              error={errors.gemeente}
            />
          </FormControl>

          <FormControl sx={{ width: '100%', p: 1 }}>
            <TextField
              placeholder="Straatnamen, huisnummers, postcodes en woonplaatsen"
              variant="standard"
              value={project.adres}
              multiline
              rows={3}
              label={`Adres (${project.adres?.length || 0}/100)`}
              onChange={(e) =>
                setProject((project) => ({ ...project, adres: e.target.value }))
              }
              error={errors.adres}
            />
          </FormControl>
          <FormControl
            sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'row' }}
          >
            <SelectObject
              disabled={isUpdateProject || !!project.linkedProjectdossier}
              sx={{ width: '50%', pr: 2 }}
              options={gebiedOptions}
              getOptionLabel={(option) => option.label}
              onChange={(gebied) => {
                const gebiedscode = gebied?.code || undefined
                setProject((project) => ({
                  ...project,
                  labels: [],
                  disciplineVerzamelingen: [],
                  gebiedscode,
                }))
                onGebiedscodeChange(gebiedscode)
              }}
              value={selectedGebied}
              label="Gebied"
              error={errors.gebiedscode}
            />

            <SelectString
              sx={{ width: '50%' }}
              options={typeWerkTypes}
              onChange={(type) =>
                setProject((project) => ({
                  ...project,
                  typeWerk:
                    type /* v8 ignore next - TODO: missing coverage -- @preserve */ ||
                    undefined,
                }))
              }
              value={project.typeWerk || null}
              label="Type werk"
              error={errors.typeWerk}
              disabled={isUpdateProject && !isEditTypeWerkAllowed}
            />
          </FormControl>

          <FormControl
            sx={{
              display: 'flex',
              p: 1,
              flexDirection: 'row',
              width: '100%',
            }}
          >
            <DatePicker
              label="Aanvraagdatum"
              value={aanvraagDatum}
              onChange={(date) => setAanvraagDatum(date)}
              displayWeekNumber
              disableFuture
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { width: '100%', pr: 2 },
                },
              }}
            />
            <TextField
              sx={{ pt: 0.4 }}
              variant="standard"
              fullWidth
              value={project.wensweek || ''}
              label="Wensweek"
              onChange={(e) => {
                setProject({
                  ...project,
                  wensweek: e.target.value,
                })
              }}
              error={
                !!project.wensweek &&
                isNaN(parseISO(project.wensweek).getTime())
              }
              placeholder="bijv. 2023-W01"
            />
          </FormControl>

          <FormControl
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              p: 1,
            }}
          >
            <DatePicker
              disabled={isUpdateProject}
              views={['year']}
              label="Jaar start uitvoering"
              value={startUitvoeringJaar || null}
              onChange={(date) => setStartUitvoeringJaar(date)}
              openTo="year"
              displayWeekNumber
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { width: '33%', pr: 2 },
                  error: Boolean(errors.startUitvoeringWeek),
                },
              }}
            />

            <TextField
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 53,
                  'data-testid': 'startUitvoeringWeek-field',
                },
              }}
              disabled={isUpdateProject}
              sx={{ width: '33%', pr: 2 }}
              variant="standard"
              type="number"
              value={startUitvoeringWeek || ''}
              label={'Week start uitvoering'}
              onChange={(e) => setStartUitvoeringWeek(Number(e.target.value))}
              error={errors.startUitvoeringWeek}
            />

            <TextField
              sx={{ width: '33%' }}
              disabled={isUpdateProject}
              slotProps={{
                htmlInput: { min: 1, 'data-testid': 'doorlooptijd-field' },
              }}
              variant="standard"
              type="number"
              value={
                typeof project.doorlooptijdWeken === 'number'
                  ? project.doorlooptijdWeken
                  : ''
              }
              label="Doorlooptijd (in weken)"
              onChange={(e) =>
                setProject((project) => ({
                  ...project,
                  doorlooptijdWeken: parseInt0(e.target.value),
                }))
              }
              error={errors.doorlooptijdWeken}
            />
          </FormControl>
          <FormControl
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              p: 1,
            }}
          >
            <SelectObject
              multiple
              sx={{ width: '50%' }}
              disabled={!project.gebiedscode || isUpdateProject}
              options={netbeheerderDisciplines as DisciplineVerzameling[]}
              getOptionLabel={(option) => {
                return `${option.netbeheerderLabel} - ${option.disciplineLabel}`
              }}
              onChange={(disc) =>
                setProject((project) => ({
                  ...project,
                  /* v8 ignore next - TODO: missing coverage -- @preserve */
                  disciplineVerzamelingen: disc || undefined,
                }))
              }
              value={
                /* v8 ignore next - TODO: missing coverage -- @preserve */
                project.disciplineVerzamelingen || null
              }
              label="Disciplines"
              error={errors.disciplineVerzamelingen}
            />
            <SelectString
              sx={{ width: '50%', pl: 1 }}
              disabled={isUpdateProject}
              options={aannemers}
              onChange={(aannemer) =>
                setProject((project) => ({
                  ...project,
                  /* v8 ignore next - TODO: missing coverage -- @preserve */
                  aannemers: aannemer ? [aannemer] : [],
                }))
              }
              value={
                project.aannemers && project.aannemers.length > 0
                  ? project.aannemers[0]
                  : null
              }
              label="Aannemer"
              error={errors.aannemers}
            />
          </FormControl>

          <FormControl
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              p: 1,
            }}
          >
            <SelectString
              multiple
              sx={{ width: '50%' }}
              disabled={!selectableLabels.length || isUpdateProject}
              options={selectableLabels}
              fullWidth
              value={
                /* v8 ignore next - TODO: missing coverage -- @preserve */
                project.labels || []
              }
              onChange={(labels) =>
                setProject({
                  ...project,
                  /* v8 ignore next - TODO: missing coverage -- @preserve */
                  labels: labels || [],
                })
              }
              label="Labels"
            />
          </FormControl>

          <FormControl
            fullWidth
            sx={{
              display: 'flex',
              flexDirection: 'row',
              p: 1,
            }}
          >
            <TextField
              fullWidth
              variant="standard"
              label="Tracelengte"
              type="number"
              slotProps={{ htmlInput: { min: 0 } }}
              sx={{
                paddingRight: 2,
              }}
              value={
                typeof project.tracelengte === 'number'
                  ? project.tracelengte
                  : 0
              }
              helperText="in meters"
              onChange={(e) =>
                setProject({
                  ...project,
                  tracelengte: parseInt0(e.target.value),
                })
              }
              error={errors.tracelengte}
            />
            <TextField
              fullWidth
              variant="standard"
              label="Aantal woningen"
              type="number"
              slotProps={{ htmlInput: { min: 0 } }}
              sx={{
                paddingRight: 2,
              }}
              value={
                typeof project.woningen === 'number' ? project.woningen : 0
              }
              helperText="Grondgebonden"
              onChange={(e) =>
                setProject({
                  ...project,
                  woningen: parseInt0(e.target.value),
                })
              }
              error={errors.woningen}
            />
            <TextField
              fullWidth
              variant="standard"
              type="number"
              label="Aantal appartementen"
              slotProps={{ htmlInput: { min: 0 } }}
              sx={{
                paddingRight: 2,
              }}
              value={
                typeof project.appartementen === 'number'
                  ? project.appartementen
                  : 0
              }
              onChange={(e) =>
                setProject({
                  ...project,
                  appartementen: Number(e.target.value),
                })
              }
              error={errors.appartementen}
            />
            <TextField
              fullWidth
              variant="standard"
              type="number"
              label="Aantal bedrijfsunits"
              slotProps={{ htmlInput: { min: 0 } }}
              value={
                typeof project.bedrijfsunits === 'number'
                  ? project.bedrijfsunits
                  : 0
              }
              onChange={(e) =>
                setProject({
                  ...project,
                  bedrijfsunits: Number(e.target.value),
                })
              }
              error={errors.bedrijfsunits}
            />
          </FormControl>

          {!isUpdateProject &&
            hasGeschovenNaarAannemer &&
            project.disciplineVerzamelingen && (
              <>
                <StyledAlert severity="warning">
                  Er zijn disciplines geselecteerd die geschoven kunnen worden
                  naar de aannemer. Geef aan of deze disciplines zijn geschoven
                  naar de aannemer.
                </StyledAlert>
                <GeschovenNaarAannemer
                  disciplines={project.disciplineVerzamelingen.filter(
                    isGeschovenNaarAannemerAllowed,
                  )}
                  handleDisciplineUpdate={handleDisciplineUpdate}
                />
              </>
            )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="primary" onClick={handleClose} autoFocus>
          Annuleren
        </Button>
        <Button
          disabled={isPending}
          onClick={handleSubmit}
          variant={isPending ? 'text' : 'contained'}
          startIcon={isPending ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isUpdateProject ? 'Wijzigen' : 'Aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
